import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MarketId } from '@velocity-exchange/common';
import { orderbookQueryKeys } from './orderbookQueryKeys';
import { L2OrderbookWithSlot } from './types';
import { useOrderbookSubscriptionContext } from 'src/providers/orderbook/OrderbookSubscriptionProvider';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * L2 orderbook data is synced from OrderbookSubscriptionProvider via queryClient.setQueryData.
 * This hook only reads from the cache - it does not fetch data itself.
 */
export function useL2Orderbook(marketId: MarketId | null) {
	const isProviderMounted = useOrderbookSubscriptionContext();
	const hasWarnedRef = useRef(false);

	useEffect(() => {
		if (
			process.env.NODE_ENV === 'development' &&
			!isProviderMounted &&
			!hasWarnedRef.current
		) {
			hasWarnedRef.current = true;
			console.warn(
				'useL2Orderbook: OrderbookSubscriptionProvider is not mounted. ' +
					'This hook will return null until the provider is available.'
			);
		}
	}, [isProviderMounted]);

	return useQuery<L2OrderbookWithSlot | null>({
		queryKey: orderbookQueryKeys.l2Orderbook(marketId?.key),
		queryFn: () => null,
		enabled: false,
		staleTime: Infinity,
		gcTime: FIVE_MINUTES_MS,
	});
}
